import { useMemo } from 'react'
import { Box, Chip, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import Status from 'components/global/Status'

import {
  availableToBgColor,
  availableToColor,
  availableToTranslationKey,
} from '../../helpers'

export const parseUptime = (uptime) => {
  const nbDay = Math.trunc(uptime)
  const hour = moment()
    .startOf('day')
    .add(24 * 3600 * (uptime - nbDay), 'seconds')
    .format('HH:mm:ss')
  return {nbDay, hour}
}

const formatDecimal = (value, digits = 1) => {
  if (value == null || value === '') return '-'
  const number = Number(value)
  if (!Number.isFinite(number)) return '-'
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  }).format(number)
}

const ValuesTable = ({ value1, value2 }) => (
  <Stack gap={0.25}>
    <Typography sx={{ color: '#464646', fontSize: 12, lineHeight: '14px' }}>{value1 || '-'}</Typography>
    {value2 && <Typography sx={{ color: '#989898', fontSize: 10, lineHeight: '12px' }}>{value2}</Typography>}
  </Stack>
)

const TagStatus = ({ equipment }) => {
  const { t } = useTranslation()
  const status = useMemo(() => {
    const trad = ['hibernation', 'works'].includes(equipment.available_level)
      ? `${equipment.available_level}_${equipment.is_reachable ? 'up' : 'down'}`
      : equipment.available_level
    return {
      label: t(availableToTranslationKey(trad)),
      color: availableToColor(equipment.available_level),
      bgcolor: availableToBgColor(equipment.available_level)
    }
  }, [equipment, t])

  return (
    <Status
      isUppercase={false}
      status={equipment.manufacturer?.toLowerCase() === 'juniper' ? '-' : status.label}
      color={status.color}
      bgcolor={status.bgcolor}
    />
  )
}

const BooleanIcon = ({ value }) => (
  <Box
    component="i"
    className={`fa-light fa-${value ? 'check' : 'ban'}`}
    sx={{ color: value ? '#92BC62' : '#DC2626', fontSize: 12 }}
  />
)

const SsidCount = ({ equipment }) => {
  const count = new Set(equipment.ssid_names || equipment.ssids?.map(({ name }) => name) || []).size
  return (
    <Chip
      label={count}
      size="small"
      sx={{
        width: 'fit-content',
        height: 16,
        borderRadius: '8px',
        backgroundColor: '#EFEFEF',
        color: '#656565',
        fontSize: 10,
        '& .MuiChip-label': { px: 1 }
      }}
    />
  )
}

const PairValue = ({ first, second, digits = 1, unit = '' }) => (
  <Typography sx={{ color: '#656565', fontSize: 12, whiteSpace: 'nowrap' }}>
    {formatDecimal(first, digits)}{unit} / {formatDecimal(second, digits)}{unit}
  </Typography>
)

const CompactUptime = ({ value }) => {
  if (value == null) return '-'
  return `${formatDecimal(value, 1)}j`
}

const PoeTableBar = ({ poe }) => {
  const measured = Number(poe?.measured)
  const reserved = Number(poe?.reserved)
  const max = Number(poe?.max)
  if (!Number.isFinite(max) || max <= 0) return '-'

  const measuredPercent = Number.isFinite(measured) ? Math.min(Math.max((measured / max) * 100, 0), 100) : 0
  const reservedPercent = Number.isFinite(reserved) ? Math.min(Math.max((reserved / max) * 100, 0), 100) : 0

  return (
    <Stack sx={{ width: 112 }} gap={0.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography sx={{ color: '#464646', fontSize: 9 }}>{Number.isFinite(measured) ? `${formatDecimal(measured, 0)}W` : '-'}</Typography>
        <Typography sx={{ color: '#F59E0B', fontSize: 9 }}>{Number.isFinite(reserved) ? `${formatDecimal(reserved, 0)}W` : '-'}</Typography>
        <Typography sx={{ color: '#656565', fontSize: 9 }}>{`${formatDecimal(max, 0)}W`}</Typography>
      </Stack>
      <Box sx={{ position: 'relative', height: 8, borderRadius: '16px', backgroundColor: '#F7F7F7', overflow: 'hidden' }}>
        <Box sx={{ width: `${measuredPercent}%`, height: '100%', borderRadius: '16px', backgroundColor: '#92BC62' }} />
        <Box sx={{ position: 'absolute', left: `calc(${reservedPercent}% - 1px)`, top: 0, width: 2, height: '100%', borderRadius: '2px', backgroundColor: '#F59E0B' }} />
      </Box>
    </Stack>
  )
}

const width = minWidth => ({ minWidth })

const statusColumn = {
  name: 'available_level',
  header: { label: ctx => ctx.t('supervision.tables.status_col') },
  value: equipment => <TagStatus equipment={equipment} />,
  sortBy: 'available_level',
  sortable: true,
  style: width(90)
}

const labelColumn = {
  name: 'label',
  header: { label: ctx => ctx.t('supervision.tables.label_col_count') },
  value: equipment => equipment.label || '-',
  sortBy: 'label',
  sortable: true,
  style: width(100)
}

const manufacturerColumn = {
  name: 'model',
  header: { label: ctx => ctx.t('supervision.tables.manufacturers_col') },
  value: equipment => <ValuesTable value1={equipment.manufacturer} value2={equipment.model} />,
  style: width(110)
}

const macColumn = {
  name: 'mac',
  header: { label: ctx => ctx.t('supervision.tables.mac_col') },
  value: equipment => equipment.mac || '-',
  style: width(100)
}

const ipColumn = {
  name: 'ip_address',
  header: { label: ctx => ctx.t('supervision.tables.ip_address_col') },
  value: equipment => equipment.ip_address || '-',
  style: width(90)
}

const serialColumn = {
  name: 'serial',
  header: { label: ctx => ctx.t('supervision.tables.serial_col') },
  value: equipment => equipment.serial || '-',
  style: width(80)
}

const locationColumn = {
  name: 'site_name',
  header: { label: ctx => ctx.t('supervision.tables.locations_col') },
  value: equipment => <ValuesTable value1={equipment.site?.name} value2={equipment.building_name} />,
  sortBy: 'site_name',
  sortable: true,
  style: width(110)
}

const floorColumn = {
  name: 'floors',
  header: { label: ctx => ctx.t('supervision.tables.floors_col') },
  value: equipment => equipment.floor_name || '-',
  style: width(80)
}

const roomColumn = {
  name: 'room',
  header: { label: ctx => ctx.t('supervision.tables.rooms_col') },
  value: equipment => equipment.room || '-',
  style: width(80)
}

const uptimeColumn = {
  name: 'system_uptime',
  header: { label: ctx => ctx.t('supervision.tables.system_uptime_col') },
  value: (equipment, ctx) => {
    if (!equipment.system_uptime) return '-'
    const {nbDay, hour} = parseUptime(equipment.system_uptime)
    return nbDay ? ctx.t('supervision.tables.system_uptime_value', {count: nbDay, hour}) : hour
  },
  style: width(90)
}

const connectedDevicesColumn = {
  name: 'connected_devices',
  header: { label: ctx => ctx.t('supervision.tables.connected_devices_col') },
  value: equipment => equipment.connected_stations ?? '-',
  style: width(90)
}

const defaultDetailedViewColumns = [statusColumn, labelColumn, manufacturerColumn, macColumn, ipColumn, locationColumn, floorColumn]

const isType = (...hardwareTypes) => ({ hardwareType }) => hardwareTypes.includes(hardwareType)

const simplifiedViewColumns = [
  { column: statusColumn },
  { column: labelColumn },
  { column: manufacturerColumn, isVisible: ({ cardOpen }) => !cardOpen },
  { column: macColumn },
  { column: serialColumn, isVisible: isType('firewalls') },
  { column: locationColumn },
  { column: floorColumn },
  { column: roomColumn, isVisible: isType('chromecasts') },
  { column: connectedDevicesColumn, isVisible: isType('access_points') },
  { column: uptimeColumn, isVisible: isType('firewalls') }
]

const detailedViewColumnsByType = {
  access_points: [
    statusColumn,
    labelColumn,
    {
      name: 'ssids',
      header: { label: ctx => ctx.t('supervision.card.ssid_name') },
      value: equipment => <SsidCount equipment={equipment} />,
      style: width(60)
    },
    manufacturerColumn,
    macColumn,
    ipColumn,
    connectedDevicesColumn,
    {
      name: 'channels',
      header: { label: ctx => ctx.t('supervision.tables.channels_col') },
      value: equipment => <PairValue first={equipment.channel_g} second={equipment.channel_a} digits={0} />,
      style: width(120)
    },
    {
      name: 'cpu_ram',
      header: { label: ctx => ctx.t('supervision.tables.cpu_ram_col') },
      value: equipment => <PairValue first={equipment.cpu_usage} second={equipment.memory_usage} />,
      style: width(80)
    },
    {
      name: 'uptime',
      header: { label: ctx => ctx.t('supervision.hardware.ap.uptime') },
      value: equipment => <CompactUptime value={equipment.system_uptime} />,
      style: width(75)
    },
    {
      name: 'reach',
      header: { label: ctx => ctx.t('supervision.tables.reach_col') },
      value: equipment => <BooleanIcon value={equipment.is_reachable} />,
      style: width(60)
    },
    {
      name: 'snmp',
      header: { label: ctx => ctx.t('supervision.hardware.ap.snmp') },
      value: equipment => <BooleanIcon value={equipment.is_snmp_reachable} />,
      style: width(60)
    }
  ],
  switches: [
    statusColumn,
    labelColumn,
    ipColumn,
    macColumn,
    manufacturerColumn,
    {
      name: 'poe',
      header: { label: ctx => ctx.t('supervision.hardware.switch.poe_state') },
      value: equipment => <PoeTableBar poe={equipment.poe} />,
      style: width(110)
    },
    {
      name: 'cpu_ram',
      header: { label: ctx => ctx.t('supervision.tables.cpu_ram_col') },
      value: equipment => <PairValue first={equipment.cpu_usage} second={equipment.memory_usage} />,
      style: width(80)
    },
    {
      name: 'uptime',
      header: { label: ctx => ctx.t('supervision.hardware.ap.uptime') },
      value: equipment => <CompactUptime value={equipment.system_uptime} />,
      style: width(75)
    },
    {
      name: 'reach',
      header: { label: ctx => ctx.t('supervision.tables.reach_col') },
      value: equipment => <BooleanIcon value={equipment.is_reachable} />,
      style: width(60)
    },
    {
      name: 'snmp',
      header: { label: ctx => ctx.t('supervision.hardware.ap.snmp') },
      value: equipment => <BooleanIcon value={equipment.is_snmp_reachable} />,
      style: width(60)
    }
  ],
  firewalls: [statusColumn, labelColumn, manufacturerColumn, macColumn, serialColumn, locationColumn, floorColumn, uptimeColumn],
  esls: defaultDetailedViewColumns,
  inverters: defaultDetailedViewColumns,
  tvs: [...defaultDetailedViewColumns, roomColumn],
  chromecasts: [...defaultDetailedViewColumns, roomColumn],
  cctv_cameras: [statusColumn, labelColumn, manufacturerColumn, macColumn, locationColumn, floorColumn],
  cctv_servers: [
    statusColumn,
    labelColumn,
    manufacturerColumn,
    macColumn,
    locationColumn,
    floorColumn,
    {
      name: 'disk_usage',
      header: { label: ctx => ctx.t('supervision.cctv.total_disk_usage') },
      value: equipment => equipment.disk_usage == null ? '-' : `${formatDecimal(equipment.disk_usage, 0)}%`,
      style: width(100)
    }
  ]
}

const getSimplifiedViewColumns = (hardwareType, cardOpen) => simplifiedViewColumns
  .filter(({ isVisible }) => !isVisible || isVisible({ hardwareType, cardOpen }))
  .map(({ column }) => ({ ...column, style: { minWidth: 0 } }))

export const getColumnsByHardwareType = (
  hardwareType,
  { isDetailedView = true, cardOpen = false } = {}
) => isDetailedView
  ? detailedViewColumnsByType[hardwareType] || defaultDetailedViewColumns
  : getSimplifiedViewColumns(hardwareType, cardOpen)
